import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/admin/Layout';
import { Button, Card, EmptyState, Spinner, StatusBadge, relativeTime } from '../../components/admin/ui';
import adminApi, { readableError } from '../../utils/adminApi';

const StatCard = ({ label, value, badge, href, accent, icon }) => (
  <Link
    to={href}
    className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
  >
    <div className={`absolute inset-x-0 top-0 h-1 ${accent}`} />
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-lg">
        {icon}
      </div>
    </div>
    {badge > 0 && (
      <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
        {badge} need attention
      </p>
    )}
  </Link>
);

// Lightweight inline bar chart - avoids pulling in a charting library.
const ActivityChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="flex h-40 items-end gap-1.5">
      {data.map((day) => {
        const label = new Date(day.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
        return (
          // h-full matters: the bar's percentage height needs a resolved parent height.
          <div key={day.date} className="group relative flex h-full flex-1 flex-col items-center justify-end">
            <div
              className="w-full rounded-t-md bg-gradient-to-t from-indigo-500 to-violet-400 transition-all group-hover:from-indigo-600 group-hover:to-violet-500"
              style={{ height: `${Math.max(4, (day.total / max) * 100)}%` }}
            />
            <div className="pointer-events-none absolute bottom-full mb-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg group-hover:block">
              <p className="font-medium">{label}</p>
              <p className="text-slate-300">
                {day.cvs} CVs · {day.contacts} contacts · {day.consultations} consults
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get('/admin/dashboard');
      setData(response.data?.data || null);
    } catch (err) {
      setError(readableError(err, 'Could not load the dashboard.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = data?.statistics;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <Layout>
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-32 text-sm text-slate-500">
          <Spinner /> Loading dashboard…
        </div>
      ) : error ? (
        <Card>
          <EmptyState
            icon="⚠️"
            title="Could not load the dashboard"
            description={error}
            action={<Button variant="primary" onClick={load}>Try again</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                Welcome back{user.name ? `, ${user.name.split(' ')[0]}` : ''}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Here is what has come in through the website.
              </p>
            </div>
            <Button onClick={load}>Refresh</Button>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Resumes" icon="📄" href="/admin/cvs"
              value={stats?.cvs?.total ?? 0} badge={stats?.cvs?.pending ?? 0}
              accent="bg-gradient-to-r from-indigo-500 to-violet-500"
            />
            <StatCard
              label="Contacts" icon="✉️" href="/admin/contacts"
              value={stats?.contacts?.total ?? 0} badge={stats?.contacts?.new ?? 0}
              accent="bg-gradient-to-r from-sky-500 to-cyan-500"
            />
            <StatCard
              label="Consultations" icon="💬" href="/admin/consultations"
              value={stats?.consultations?.total ?? 0} badge={stats?.consultations?.new ?? 0}
              accent="bg-gradient-to-r from-emerald-500 to-teal-500"
            />
            <StatCard
              label="Jobs" icon="💼" href="/admin/jobs"
              value={stats?.jobs?.total ?? 0} badge={0}
              accent="bg-gradient-to-r from-amber-500 to-orange-500"
            />
          </div>

          {/* Activity */}
          <Card className="p-5">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Submissions</h2>
                <p className="text-sm text-slate-500">Last 14 days across all forms</p>
              </div>
              <p className="text-2xl font-semibold text-slate-900">
                {data?.dailyStats?.reduce((sum, d) => sum + d.total, 0) ?? 0}
              </p>
            </div>
            {data?.dailyStats?.length ? (
              <ActivityChart data={data.dailyStats} />
            ) : (
              <p className="py-10 text-center text-sm text-slate-400">No activity yet</p>
            )}
          </Card>

          {/* Recent lists */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <RecentPanel
              title="Latest resumes"
              href="/admin/cvs"
              items={data?.recent?.cvs}
              emptyText="No CVs submitted yet"
              renderPrimary={(r) => `${r.firstName} ${r.lastName}`}
              renderSecondary={(r) => r.position || r.email}
            />
            <RecentPanel
              title="Latest contacts"
              href="/admin/contacts"
              items={data?.recent?.contacts}
              emptyText="No enquiries yet"
              renderPrimary={(r) => r.name}
              renderSecondary={(r) => r.subject || r.email}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

const RecentPanel = ({ title, href, items, emptyText, renderPrimary, renderSecondary }) => (
  <Card className="overflow-hidden">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <Link to={href} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
        View all
      </Link>
    </div>
    {items?.length ? (
      <ul className="divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 px-5 py-3.5 transition hover:bg-slate-50">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{renderPrimary(item)}</p>
              <p className="truncate text-xs text-slate-500">{renderSecondary(item)}</p>
            </div>
            <StatusBadge status={item.status} />
            <span className="whitespace-nowrap text-xs text-slate-400">
              {relativeTime(item.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="px-5 py-10 text-center text-sm text-slate-400">{emptyText}</p>
    )}
  </Card>
);

export default Dashboard;
